import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  InputAdornment,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Swal from "sweetalert2";
import {
  companyManagementUrl,
  customerservice,
  loanManagementUrl,
  setupManagementUrl,
  AssetFinanceUrl,
} from "../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import axios from "axios";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const SectionHeader = ({ title }) => (
  <>
    <Typography
      variant="overline"
      sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 1 }}
    >
      {title}
    </Typography>
    <Divider sx={{ mb: 1.5, mt: 0.25 }} />
  </>
);

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500}>
      {value ?? "N/A"}
    </Typography>
  </Box>
);

const BookAssetLoan = () => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [amount, setAmount] = useState("");
  const [loanProductSelected, setLoanProductSelected] = useState([]);
  const [loanProduct, setLoanProduct] = useState([]);
  const [customers, setCustomers] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [branchData, setBranchData] = useState("");
  const [installmentError, setInstallmentError] = useState("");
  const [noOfInstallments, setNoOfInstallments] = useState(0);
  const [collateralAssets, setCollateralAssets] = useState([]);
  const [collateralAssetsSelected, setCollateralAssetsSelected] = useState([]);
  const [lienBlockedAssets, setLienBlockedAssets] = useState([]);
  const [loadingCollateralAssets, setLoadingCollateralAssets] = useState(false);
  const base_url = customerservice.uri;

  const resetForm = () => {
    setSearchTerm("");
    setFilteredCustomer(null);
    setAmount("");
    setLoanProductSelected("");
    setNoOfInstallments("");
    setInstallmentError("");
    setCollateralAssets([]);
    setCollateralAssetsSelected([]);
    setLienBlockedAssets([]);
  };
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 10) {
        const variations = [
          searchTerm,
          searchTerm.startsWith("0") ? searchTerm.replace(/^0/, "254") : null,
          searchTerm.startsWith("254") ? searchTerm.replace(/^254/, "0") : null,
          searchTerm.startsWith("01") ? searchTerm.replace(/^01/, "254") : null, // Handle numbers starting with 01
        ].filter(Boolean);

        setLoadingSearch(true);

        try {
          let foundCustomer = null;

          for (const number of variations) {
            const query = `
              query {
                customersByColumnValues(customer: {mobileNumber: "${number}"}) {
                  error
                  results
                }
              }
            `;

            const token = localStorage.getItem("token");
            const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;

            const response = await axios.post(
              base_url,
              { query },
              {
                headers: {
                  Authorization: formattedToken,
                  "Content-Type": "application/json",
                },
              },
            );

            const data = response.data?.data?.customersByColumnValues;

            if (data?.results) {
              const parsedResults = JSON.parse(data?.results);
              if (parsedResults.length) {
                foundCustomer = parsedResults[0];
                break; // Exit the loop if customer is found
              }
            } else if (data?.error) {
              Swal.fire("Error", data.error || "Unexpected error", "error");
              break; // Exit the loop on server-side error
            }
          }

          if (foundCustomer) {
            setFilteredCustomer(foundCustomer); // Add balance and collateral to customer object
          } else {
            Swal.fire(
              "Customer Not Found",
              "Customer does not exist with any variations.",
              "warning",
            );
          }
          const params = {
            companyId: parseInt(companyId),
            isSecuredProduct: true,
          };
          const productResponse = await fetchJsonDynamic(
            "individualProducts",
            "parameters",
            params,
            setupManagementUrl.uri,
          );

          const allProduct = JSON.parse(productResponse.results) || [];

          // Filter regions based on selected company ID

          setLoanProduct(allProduct || []);

          if (!foundCustomer) {
            Swal.fire(
              "Customer Not Found",
              "Customer does not exist with any variations.",
              "warning",
            );
          }
        } catch (error) {
          Swal.fire(
            "Error",
            "An error occurred while searching. Please try again.",
            "error",
          );
          console.error("Error fetching customer by mobile number:", error);
        } finally {
          setLoadingSearch(false);
        }
      } else {
        setFilteredCustomer(null);
        setLoadingSearch(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    const fetchCollateralAssets = async () => {
      setCollateralAssetsSelected([]);
      setLienBlockedAssets([]);
      if (!filteredCustomer?.Id) {
        setCollateralAssets([]);
        return;
      }
      setLoadingCollateralAssets(true);
      try {
        const params = {
          isEligibleForLoan: true,
          customerId: parseInt(filteredCustomer.Id),
        };
        const response = await fetchJsonDynamic(
          "collateralValuationByColumnValues",
          "valuation",
          params,
          AssetFinanceUrl.uri,
        );
        const assets = response?.results ? JSON.parse(response.results) : [];

        // Assets that require a lien can only back a loan once that lien is
        // REGISTERED with the registry — verify each one before offering it.
        const eligible = [];
        const blocked = [];

        await Promise.all(
          (assets || []).map(async (asset) => {
            if (!asset.RequireLien) {
              eligible.push(asset);
              return;
            }
            try {
              const lienResponse = await fetchJsonDynamic(
                "lienRegistrationsByColumnValues",
                "lien",
                { assetId: asset.AssetId },
                AssetFinanceUrl.uri,
              );
              const liens = lienResponse?.results ? JSON.parse(lienResponse.results) : [];
              const hasRegisteredLien = (Array.isArray(liens) ? liens : []).some(
                (lien) => lien.LienStatus === "REGISTERED",
              );
              if (hasRegisteredLien) {
                eligible.push(asset);
              } else {
                blocked.push(asset);
              }
            } catch (error) {
              blocked.push(asset);
            }
          }),
        );

        setCollateralAssets(eligible);
        setLienBlockedAssets(blocked);
      } catch (error) {
        console.error("Error fetching collateral assets:", error);
        setCollateralAssets([]);
        setLienBlockedAssets([]);
      } finally {
        setLoadingCollateralAssets(false);
      }
    };
    fetchCollateralAssets();
  }, [filteredCustomer]);

  const handleAssetToggle = (assetId) => {
    setCollateralAssetsSelected((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId],
    );
  };

  const handleAppraisalSubmit = async () => {
    // Validate installments if dynamic installment is enabled
    if (selectedLoanProduct.IsdynamicInstallment === 1) {
      const min = selectedLoanProduct.MinimumTerm || 0;
      const max = selectedLoanProduct.MaximumTerm || Infinity;
      const numInstallments = parseInt(noOfInstallments, 10);

      if (!noOfInstallments || isNaN(numInstallments)) {
        setInstallmentError("Please enter a valid number of installments");
        return;
      }

      if (numInstallments < min || numInstallments > max) {
        setInstallmentError(`Installments must be between ${min} and ${max}`);
        return;
      }
    }

    if (!collateralAssetsSelected.length) {
      Swal.fire({
        icon: "warning",
        title: "Collateral Required",
        text: "Please select at least one collateral asset for this loan.",
      });
      return;
    }

    setLoadingSubmit(true); // Start loading when the form is submitted

    try {
      const mutationData = {
        p_CustomerId: filteredCustomer.Id,
        p_Principle: parseInt(amount),
        p_ProductId: loanProductSelected,
        p_Installment: parseInt(noOfInstallments, 10) || null,
        p_CollateralAssetIds: collateralAssetsSelected,
      };

      const response = await executeAnyMutation(
        "insertLoanApplications",
        mutationData,
        "loanApplication",
        false,
        AssetFinanceUrl.uri,
      );
      const parsedResults = JSON.parse(response.results);
      if (parsedResults && parsedResults.length) {
        if (parsedResults[0] === "Loan Booked Successfully") {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: parsedResults[0],
          });
          // Reset form upon successful submission
          resetForm();
        } else {
          // The backend can return several decline reasons at once (loan limit,
          // product range, LTV, etc.) — render each on its own line instead of
          // letting them collapse into one comma-joined string.
          const escapeHtml = (value) =>
            String(value)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");
          Swal.fire({
            icon: "error",
            title: parsedResults.length > 1 ? "Loan Declined" : "Error!",
            html: `<ul style="text-align:left; margin:0; padding-left:1.2em;">${parsedResults
              .map((reason) => `<li>${escapeHtml(reason)}</li>`)
              .join("")}</ul>`,
          });
        }
      } else if (response.errors) {
        throw new Error(
          response.errors[0].Error || "An internal server error occurred.",
        );
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
      resetForm();
    } finally {
      setLoadingSubmit(false); // End loading after the process
    }
  };

  const date = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const selectedLoanProduct =
    loanProduct.find((product) => product.Id === loanProductSelected) || {};

  const getCollateralAssetLabel = (asset) => {
    const parts = [asset.AssetTypeName, asset.SubTypeName].filter(Boolean);
    try {
      const metadata = asset.Metadata ? JSON.parse(asset.Metadata) : {};
      if (metadata.regNumber) parts.push(metadata.regNumber);
    } catch (error) {
      // Metadata isn't valid JSON — fall back to the type/sub-type label only.
    }
    return parts.join(" - ") || `Asset #${asset.AssetId}`;
  };

  const totalForcedSaleValue = collateralAssets
    .filter((asset) => collateralAssetsSelected.includes(asset.AssetId))
    .reduce((sum, asset) => sum + (Number(asset.ForcedSaleValue) || 0), 0);

  return (
    <Box sx={{ padding: 3, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700}>
        Book A Loan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Search a customer by phone number to begin a new loan application.
      </Typography>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 2 }}>
        <TextField
          label="Search Customer by Phone Number"
          placeholder="e.g. 0712345678"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: loadingSearch && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {filteredCustomer && (
        <>
          <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <SectionHeader title="Customer Details" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    label="Customer Name"
                    value={`${filteredCustomer.FirstName || ""} ${filteredCustomer.OtherNames || ""}`}
                  />
                  <Field label="Email" value={filteredCustomer.EmailAddress} />
                  <Field label="ID Number" value={filteredCustomer.IdNoOrPassport} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label="Mobile Number" value={filteredCustomer.MobileNumber} />
                  <Field label="Branch" value={filteredCustomer.BranchName} />
                  <Field
                    label="Date Created"
                    value={
                      filteredCustomer.DateCreated
                        ? formatter.format(new Date(filteredCustomer.DateCreated))
                        : "N/A"
                    }
                  />
                  <Field
                    label="Current Loan Limit"
                    value={
                      filteredCustomer.LoanLimit != null
                        ? formatCurrency(filteredCustomer.LoanLimit)
                        : "N/A"
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <SectionHeader title="Loan Details" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Loan Product</InputLabel>
                    <Select
                      value={loanProductSelected}
                      onChange={(e) => setLoanProductSelected(e.target.value)}
                      defaultValue=""
                      label="Loan Product"
                    >
                      <MenuItem value="" disabled>
                        Select Loan Product
                      </MenuItem>
                      {loanProduct.map((company) => (
                        <MenuItem key={company.Id || 0} value={company.Id || 0}>
                          {company.Name || ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Amount to Disburse"
                    placeholder="Enter Amount"
                    type="text"
                    inputMode="numeric"
                    value={amount === "" ? "" : Number(amount).toLocaleString()}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
                      setAmount(raw);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">KES</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Show No of Installments only if IsDynamicInstallment === 1 */}
              {selectedLoanProduct.IsdynamicInstallment === 1 && (
                <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 1.5, mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Minimum Installment"
                        value={selectedLoanProduct.MinimumTerm || ""}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Maximum Installment"
                        value={selectedLoanProduct.MaximumTerm || ""}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="No of Installments"
                        placeholder="Enter Number of Installments"
                        value={noOfInstallments}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only check if it's a valid number (not empty or numeric)
                          if (value === "" || /^\d*$/.test(value)) {
                            setNoOfInstallments(value);
                            setInstallmentError(""); // Clear error when user types
                          }
                        }}
                        error={!!installmentError}
                        helperText={
                          installmentError ||
                          `Enter between ${selectedLoanProduct.MinimumTerm} and ${selectedLoanProduct.MaximumTerm}`
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <SectionHeader title="Collateral Assets" />

              {loadingCollateralAssets ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Checking eligible assets...
                  </Typography>
                </Box>
              ) : collateralAssets.length === 0 ? (
                <Alert severity={lienBlockedAssets.length > 0 ? "warning" : "info"}>
                  {lienBlockedAssets.length > 0
                    ? "All eligible assets require lien registration first."
                    : "No eligible collateral assets found for this customer."}
                </Alert>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 2, maxHeight: 320, overflowY: "auto" }}
                >
                  <List dense disablePadding>
                    {collateralAssets.map((asset, index) => {
                      const checked = collateralAssetsSelected.includes(asset.AssetId);
                      return (
                        <React.Fragment key={asset.Id}>
                          {index > 0 && <Divider component="li" />}
                          <ListItem
                            disablePadding
                            secondaryAction={
                              <Chip
                                size="small"
                                label={formatCurrency(asset.ForcedSaleValue)}
                                color={checked ? "primary" : "default"}
                                variant={checked ? "filled" : "outlined"}
                                sx={{ mr: 1, fontWeight: 600 }}
                              />
                            }
                          >
                            <ListItemButton
                              onClick={() => handleAssetToggle(asset.AssetId)}
                              dense
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  checked={checked}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText primary={getCollateralAssetLabel(asset)} />
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              )}

              {collateralAssetsSelected.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {collateralAssetsSelected.length} asset
                    {collateralAssetsSelected.length > 1 ? "s" : ""} selected
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {collateralAssetsSelected.length > 1
                      ? "Aggregate Forced Sale Value: "
                      : "Forced Sale Value: "}
                    {formatCurrency(totalForcedSaleValue)}
                  </Typography>
                </Box>
              )}

              {lienBlockedAssets.length > 0 && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
                  sx={{ mt: 1.5 }}
                >
                  {lienBlockedAssets.length === 1
                    ? `${getCollateralAssetLabel(lienBlockedAssets[0])} cannot be used as collateral yet.`
                    : `${lienBlockedAssets.length} asset(s) cannot be used as collateral yet.`}{" "}
                  The lien must be registered first — go to{" "}
                  <strong>Asset Loan Management &gt; Lien Management</strong> to register it.
                </Alert>
              )}
            </CardContent>
          </Card>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{ mt: 1, borderRadius: 2, py: 1.5 }}
            onClick={handleAppraisalSubmit}
            disabled={
              loadingSubmit ||
              !collateralAssetsSelected.length ||
              (selectedLoanProduct.IsdynamicInstallment === 1 &&
                (!noOfInstallments ||
                  noOfInstallments < selectedLoanProduct.MinInstallments ||
                  noOfInstallments > selectedLoanProduct.MaxInstallments))
            }
          >
            {loadingSubmit ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit For Appraisal"
            )}
          </Button>
        </>
      )}
    </Box>
  );
};

export default BookAssetLoan;
